import React, { useEffect, useState } from 'react';
import { Modal, Button, useToast, Typography } from '@crisellevaldez/speculo-ui';
import { useQueryClient } from '@tanstack/react-query';
import { getFeatureFlag, setFeatureFlag } from '../../../api/featureFlags';
import { Organization } from '../../../types/organization.types';

interface AskRemiConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
}

export const AskRemiConfigModal: React.FC<AskRemiConfigModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !selectedOrg?.id) return;
    let cancelled = false;
    setIsLoading(true);
    getFeatureFlag(selectedOrg.id, 'ask_remi')
      .then((value) => { if (!cancelled) setEnabled(value); })
      // A missing flag returns enabled=false from the backend; treat any
      // other fetch error as "disabled" so admins can flip it on explicitly.
      .catch(() => { if (!cancelled) setEnabled(false); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, selectedOrg?.id]);

  const handleSave = async () => {
    if (!selectedOrg?.id) return;
    setIsSaving(true);
    try {
      await setFeatureFlag(selectedOrg.id, 'ask_remi', enabled);
      queryClient.invalidateQueries({ queryKey: ['feature-flag', selectedOrg.id, 'ask_remi'] });
      showToast({
        message: enabled ? 'Ask Remi enabled for this organization' : 'Ask Remi disabled for this organization',
        variant: 'success',
      });
      onClose();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update Ask Remi setting',
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedOrg) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <Typography variant="h3">Ask Remi</Typography>
      </Modal.Header>
      <Modal.Body className="py-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h4 className="text-sm font-bold text-foreground">Enable Ask Remi</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {enabled
                ? 'Available to users in this organization'
                : 'Hidden for users in this organization'}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={isLoading || isSaving}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-white/10 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
          </label>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={isLoading}>
            Save Configuration
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AskRemiConfigModal;
